import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CompanyLogo } from "@/components/CompanyLogo";
import { PoweredBy } from "@/components/PoweredBy";
import { toast } from "sonner";
import { ClipboardCheck, DoorOpen, LifeBuoy, LogOut, ChevronRight, Loader2, LayoutDashboard } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CabinPortal() {
  const navigate = useNavigate();
  const [cabin, setCabin] = useState(() => { try { return JSON.parse(localStorage.getItem("sf_cabin") || "null"); } catch { return null; } });
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("sf_cabin_token");
    if (!token) { navigate("/login"); return; }
    axios.get(`${API}/public/cabin/session/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { setCabin(r.data); localStorage.setItem("sf_cabin", JSON.stringify(r.data)); })
      .catch(() => { logout(); })
      .finally(() => setChecking(false));
  }, []);

  const logout = () => {
    const pid = cabin?.public_id;
    localStorage.removeItem("sf_cabin_token"); localStorage.removeItem("sf_cabin");
    navigate(pid ? `/cabin/${pid}` : "/login");
  };

  if (checking && !cabin) return <div className="min-h-screen flex items-center justify-center bg-slate-100"><Loader2 className="animate-spin text-navy-900" /></div>;
  if (!cabin) return null;

  const services = [
    { key: "visitor", n: "01", title: "Visitor Management", desc: "Pre-request or approve visitors.", icon: ClipboardCheck, go: () => navigate("/cabin-visitors") },
    { key: "booking", n: "02", title: "Meeting Room Booking", desc: "See upcoming bookings and book a room.", icon: DoorOpen, go: () => navigate("/cabin-meetings") },
    { key: "ticket", n: "03", title: "Service Request", desc: "Raise and track service issues.", icon: LifeBuoy, go: () => navigate("/cabin-tickets") },
    { key: "dashboard", n: "04", title: "View Dashboard", desc: "Visitors, meetings and requests at a glance.", icon: LayoutDashboard, go: () => navigate("/cabin-dashboard") },
  ];

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-navy-900 px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {cabin.logo_url ? <CompanyLogo logoUrl={cabin.logo_url} className="h-8" light alt={cabin.company} /> : <span className="text-white font-display font-bold">{cabin.company}</span>}
        </div>
        <button onClick={logout} data-testid="cabin-logout-btn" className="text-blue-100/80 hover:text-white flex items-center gap-1.5 text-sm"><LogOut size={16} /> Logout</button>
      </header>

      <main className="max-w-lg mx-auto px-4 py-8">
        <div className="mb-6">
          <p className="text-sm text-slate-500">Welcome back{cabin.member_name ? `, ${cabin.member_name}` : ""}</p>
          <h1 className="font-display text-2xl font-bold text-navy-900">{cabin.member_company}</h1>
          <p className="text-xs text-slate-400 mt-0.5 font-mono">{cabin.cabin_code} · {cabin.branch}</p>
        </div>

        <div className="space-y-3">
          {services.map((s) => (
            <button key={s.key} onClick={s.go} data-testid={`cabin-service-${s.key}`}
              className="w-full text-left bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4 hover:border-navy-300 hover:shadow-md transition-all">
              <div className="w-12 h-12 rounded-xl bg-navy-50 text-navy-900 flex items-center justify-center flex-shrink-0"><s.icon size={22} /></div>
              <div className="flex-1">
                <p className="text-[11px] font-semibold text-blue-600">{s.n}</p>
                <p className="font-semibold text-navy-900">{s.title}</p>
                <p className="text-sm text-slate-500">{s.desc}</p>
              </div>
              <ChevronRight className="text-slate-300" />
            </button>
          ))}
        </div>

        <div className="mt-8 flex justify-center"><PoweredBy /></div>
      </main>
    </div>
  );
}
